import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX, ReactNode } from 'react';

import { Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { Role } from 'Components/Context/RBACContext';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { ICON_FEATURE_AI_GOVERNANCE, ICON_FEATURE_CONTROL_AUTOMATION, ICON_FEATURE_GLOBAL, ICON_FEATURE_RISK_MANAGEMENT, ICON_FEATURE_TPRM, ICON_VIEW } from 'Config/Icons';
import { EXPORTS_ACTIONS, EXPORTS_COMPLIANCE_REQUIREMENTS, EXPORTS_EXCEPTIONS, EXPORTS_GOVERNANCE, EXPORTS_ISSUES, EXPORTS_OPERATIONAL_CONTROLS, EXPORTS_OPERATIONAL_CONTROLS_ASSESSMENTS, EXPORTS_OPERATIONAL_CONTROLS_EVIDENCE, EXPORTS_RISKS, EXPORTS_RISKS_REVIEWS, EXPORTS_TPRM_DUE_DILIGENCE_WORKFLOWS, EXPORTS_TPRM_THIRD_PARTIES, EXPORTS_TPRM_THIRD_PARTY_SERVICES, EXPORTS_UCRM_BUSINESSES, EXPORTS_UCRM_DUE_DILIGENCE_WORKFLOWS, EXPORTS_UCRM_USE_CASES, EXPORTS_USERS } from 'Config/PathTemplates';
import { CONTROL_AUTOMATION_ROLES, RISK_MANAGER_ROLES, RISK_ROLES, TPRM_ROLES, UCRM_ROLES } from 'Helpers/UserUtils';
import { AdvancedFeature } from 'Models/ClientDetailsModel';

import styles from './ExportListing.module.css';

export const ExportListing = (): JSX.Element => {
    const cachedData = useCachedData();

    return (
        <PageLayoutDefault
            headerTitle="Export Selection"
            body={[
                {
                    content: (
                        <>
                            <ExportSection title="Global Features" roles={[Role.REPORTING_USER]} icon={ICON_FEATURE_GLOBAL}>
                                <ExportRow title="Issues" description="View and/or download an export of all issues." url={EXPORTS_ISSUES} />
                                <ExportRow title="Exceptions" description="View and/or download an export of all exceptions." url={EXPORTS_EXCEPTIONS} />
                                <ExportRow title="Users" description="View and/or download an export of all users." url={EXPORTS_USERS} />
                            </ExportSection>
                            <ExportSection title="Risk Management" roles={[Role.REPORTING_USER, ...RISK_ROLES]} icon={ICON_FEATURE_RISK_MANAGEMENT}>
                                <ExportRow title="Risks" description="View and/or download an export of all risks." url={EXPORTS_RISKS} />
                                <ExportRow roles={[Role.REPORTING_USER, ...RISK_MANAGER_ROLES]} title="Risk Reviews" description="View and/or download an export of all risk reviews." url={EXPORTS_RISKS_REVIEWS} />
                                <ExportRow title="Actions" description="View and/or download an export of all actions." url={EXPORTS_ACTIONS} />
                            </ExportSection>
                            <ExportSection title="Control Automation" roles={[Role.REPORTING_USER, ...CONTROL_AUTOMATION_ROLES]} icon={ICON_FEATURE_CONTROL_AUTOMATION}>
                                <ExportRow title="Operational Controls" description="View and/or download an export of all operational controls." url={EXPORTS_OPERATIONAL_CONTROLS} />
                                <ExportRow title="Control Evidence" description="View and/or download an export of all uploaded evidence for all operational controls." url={EXPORTS_OPERATIONAL_CONTROLS_EVIDENCE} />
                                <ExportRow title="Control Assessments" description="View and/or download an export of all in-progress and completed assessments for all operational controls." url={EXPORTS_OPERATIONAL_CONTROLS_ASSESSMENTS} />
                                <ExportRow title="Governance Integration" description="View and/or download an export of all standards, policies, and procedures." url={EXPORTS_GOVERNANCE} />
                                <ExportRow title="Compliance Requirements" description="View and/or download an export of all compliance requirements." url={EXPORTS_COMPLIANCE_REQUIREMENTS} />
                            </ExportSection>
                            <ExportSection title="Third-Party Risk Management" roles={[Role.REPORTING_USER, ...TPRM_ROLES]} icon={ICON_FEATURE_TPRM}>
                                <ExportRow title="Third Parties" description="View and/or download an export of all third parties." url={EXPORTS_TPRM_THIRD_PARTIES} />
                                <ExportRow title="Third-Party Services" description="View and/or download an export of all third-party services." url={EXPORTS_TPRM_THIRD_PARTY_SERVICES} />
                                <ExportRow title="Third-Party Service Due Diligence Workflows" description="View and/or download an export of all in-progress and completed due diligence workflows for all third-party services." url={EXPORTS_TPRM_DUE_DILIGENCE_WORKFLOWS} />
                            </ExportSection>
                            {cachedData.clientHasAdvancedFeature([AdvancedFeature.UCRM]) && (
                                <ExportSection title="AI Governance" roles={[Role.REPORTING_USER, ...UCRM_ROLES]} icon={ICON_FEATURE_AI_GOVERNANCE}>
                                    <ExportRow title="Businesses" description="View and/or download an export of all businesses." url={EXPORTS_UCRM_BUSINESSES} />
                                    <ExportRow title="Use Cases" description="View and/or download an export of all use cases." url={EXPORTS_UCRM_USE_CASES} />
                                    {/* The data for this export comes from an endpoint that is not available to reporting users. */}
                                    <ExportRow roles={UCRM_ROLES} title="Use Case Due Diligence Workflows" description="View and/or download an export of all in-progress and completed due diligence workflows for all use cases." url={EXPORTS_UCRM_DUE_DILIGENCE_WORKFLOWS} />
                                </ExportSection>
                            )}
                        </>
                    ),
                },
            ]}
        />
    );
};

interface ExportRowProps {
    title: string;
    description: string;
    url: string;

    roles?: Role[];
}

/**
 * A helper component that renders a row for a specific kind of export.
 */
const ExportRow = (props: ExportRowProps) => {
    const rowContent = (
        <div className={styles.settingRowContainer}>
            <div className={styles.settingDescription}>
                <Text variant="Header3">{props.title}</Text>
                <Text variant="Text3" noStyles>
                    {props.description}
                </Text>
            </div>
            <Link variant="secondaryButton" to={props.url} fontAwesomeImage={ICON_VIEW}>
                Preview
            </Link>
        </div>
    );

    return props.roles ? <RBACComponent roles={props.roles}>{rowContent}</RBACComponent> : rowContent;
};

interface ExportSectionProps {
    children: ReactNode;
    icon: IconDefinition;
    roles: Role[];
    title: string;
}

/**
 * A helper component that renders a section of rows. The entire section is hidden if the user does not have at least one of the given `roles`.
 */
const ExportSection = (props: ExportSectionProps) => {
    return (
        <RBACComponent roles={props.roles}>
            <div className={styles.icon}>
                <FontAwesomeIcon icon={props.icon} />
                <Text variant="Header4" noStyles>
                    {props.title}
                </Text>
            </div>
            {props.children}
            <hr className={styles.hr} />
        </RBACComponent>
    );
};
